/**
 * ChatInputContainer 组件
 * 整合所有聊天输入相关组件，包括文本输入、语音输入和文件上传
 */
import React, { memo, useCallback, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { UppyProvider } from './UppyProvider';
import { IOSFilePreviewArea } from './IOSFilePreviewArea';
import { FileUploadButton } from './FileUploadButton';
import { TextInput } from './TextInput';
import { VoiceInput } from './VoiceInput';
import { LongPressRecordButton } from '../audio/LongPressRecordButton';
// import { AutoTTSToggle } from './AutoTTSToggle';
import { SendButton } from './SendButton';
import { IOSQuickPrompts } from '../chat/IOSQuickPrompts';
import { IOSBottomContainer } from '../ios/IOSBottomContainer';
import {
  messageSendingAtom,
  messageCountAtom,
  selectedConversationIdAtom
} from '../../../../atoms/v3/chatAtoms';
import { triggerChatMessageAtom } from '../../../../atoms/v3/tirggerChatMessageAtom';
import { chatInputAtom, voiceModeAtom } from '../../../../atoms/v3/chatInputAtoms';
import { isTouchDevice } from '../../../../utils/platformUtils';
import { useTTS } from '../../../../hooks/v3/chat/useTTS';

interface ChatInputContainerProps {
  // 是否嵌入在桌面模式的侧边栏中
  isEmbedded?: boolean;
  // quickbar 模式：作为假门使用，点击后跳转到 chat
  quickbarMode?: boolean;
  // quickbar 模式下的回调函数
  onNewTask?: () => void;
  onNewProject?: () => void;
  onFocusNavigateToChat?: () => void;
  onFileUploadNavigateToChat?: () => void;
  onVoiceNavigateToChat?: () => void;
}

const ChatInputContainerComp: React.FC<ChatInputContainerProps> = ({
  isEmbedded = false,
  quickbarMode = false,
  onNewTask,
  onNewProject,
  onFocusNavigateToChat,
  onFileUploadNavigateToChat,
  onVoiceNavigateToChat
}) => {
  const messageCount = useAtomValue(messageCountAtom)
  const isSending = useAtomValue(messageSendingAtom);
  const sendChatMessage = useSetAtom(triggerChatMessageAtom);
  const selectedConversationId = useAtomValue(selectedConversationIdAtom);
  const voiceMode = useAtomValue(voiceModeAtom);
  const setVoiceMode = useSetAtom(voiceModeAtom);
  const isTouch = isTouchDevice();
  // 只获取setter，不订阅input值的变化
  const setInput = useSetAtom(chatInputAtom);

  // 使用 TTS hook
  const { synthesizeAndPlay } = useTTS();

  // 用于跟踪录音意图的状态
  const recordingIntentRef = React.useRef<'send' | 'edit' | 'cancelled'>('cancelled');

  // 录音完成后的回调函数（发送模式）
  const handleRecordingComplete = useCallback((blob: Blob, duration: number) => {
    console.log('录音完成，准备发送...', { size: blob.size, duration });
    recordingIntentRef.current = 'send';
  }, []);

  const handleRecordingCancel = useCallback(() => {
    console.log('录音已取消');
    recordingIntentRef.current = 'cancelled';
  }, []);

  // 录音完成后的回调函数（编辑模式）
  const handleRecordingEdit = useCallback((blob: Blob, duration: number) => {
    console.log('🔥 ChatInputContainer: handleRecordingEdit 被调用', { size: blob.size, duration });
    recordingIntentRef.current = 'edit';
  }, []);

  // STT转录完成后的统一回调
  const handleTranscriptionComplete = useCallback((text: string) => {
    console.log('STT转录完成:', text, '当前意图:', recordingIntentRef.current);

    if (recordingIntentRef.current === 'send') {
      // 发送模式：设置文本并发送
      setInput(text);
      setTimeout(() => {
        sendChatMessage();
      }, 100);
    } else if (recordingIntentRef.current === 'edit') {
      // 编辑模式：设置文本并切换到文本模式
      setInput(text);
      setVoiceMode(false);
    } else {
      // 取消模式：忽略STT结果
      console.log('录音已取消，忽略STT结果');
    }

    // 重置意图
    recordingIntentRef.current = 'cancelled';
  }, [setInput, sendChatMessage, setVoiceMode]);

  // 监听自定义 TTS 触发事件
  useEffect(() => {
    const handleTTSEvent = (event: CustomEvent) => {
      const { text, messageId } = event.detail;
      console.log('TTS: 收到触发事件，消息ID:', messageId, '文本长度:', text.length);
      synthesizeAndPlay(text);
    };

    window.addEventListener('triggerTTS', handleTTSEvent as EventListener);

    return () => {
      window.removeEventListener('triggerTTS', handleTTSEvent as EventListener);
    };
  }, [synthesizeAndPlay]);

  // 判断是否显示快速提示
  // 新对话状态下也显示快速提示，与 NewConversationPrompts 功能互补
  // quickbarMode 下不显示快速提示
  const isNewConversation = !selectedConversationId && messageCount === 0;
  const shouldShowQuickPrompts = !quickbarMode && (isNewConversation || (selectedConversationId));

  // 处理快速提示选择 - 使用useCallback优化
  const handleQuickPromptSelect = useCallback((prompt: string) => {
    if (isSending) return;
    // 设置输入内容为选中的提示
    setInput(prompt);
    // 使用 sendChatMessage 发送消息
    sendChatMessage();
  }, [ isSending, setInput, sendChatMessage]);

  // quickbarMode 下使用 IOSBottomContainer 包装
  if (quickbarMode) {
    return (
      <UppyProvider>
        <IOSBottomContainer
          mode="mobile"
          className="border-t border-gray-200 dark:border-ios-dark-gray4 bg-white dark:bg-ios-dark-gray6 pt-1"
        >
          <div className={`px-2 py-1 ${isEmbedded ? '' : 'pb-[calc(0.5rem)]'}`}>
            <div className="flex items-center">
              {/* 输入区域 - quickbar 模式 */}
              <>
                <div className="flex-shrink-0">
                  <FileUploadButton
                    onClick={onFileUploadNavigateToChat}
                  />
                </div>

                <div className="w-2"></div>

                <div className="flex-shrink-0">
                  <VoiceInput
                    onClick={onVoiceNavigateToChat}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <TextInput
                    isEmbedded={isEmbedded}
                    quickbarMode={quickbarMode}
                    onFocusNavigateToChat={onFocusNavigateToChat}
                  />
                </div>

                <div className="flex-shrink-0 flex items-center">
                  <SendButton
                    quickbarMode={quickbarMode}
                    onNewTask={onNewTask}
                    onNewProject={onNewProject}
                  />
                </div>
              </>
            </div>
          </div>
        </IOSBottomContainer>
      </UppyProvider>
    );
  }

  return (
    <UppyProvider>
      <div className="bg-white/80 dark:bg-ios-dark-gray6/80 backdrop-blur-ios border-t border-ios-gray5 dark:border-ios-dark-gray5 relative z-50">
        {/* 快速提示 */}
        {shouldShowQuickPrompts && (
          <IOSQuickPrompts onSelectPrompt={handleQuickPromptSelect} />
        )}

        {/* 文件预览区域 - iOS风格 */}
        <IOSFilePreviewArea />

        {/* 输入区域 - iOS Messages 风格 */}
        <div className={`flex items-center px-2 py-1 ${isEmbedded ? '' : 'pb-[calc(0.5rem)]'} relative z-50 ${voiceMode ? 'justify-center' : ''}`}>
          {voiceMode ? (
            // 语音模式布局：[语音/文本切换] [自动朗读开关] [语音录制按钮]
            <>

              <VoiceInput/>

              {/* 暂时下线文字转语音功能 */}
              {/* <AutoTTSToggle/> */ } 

              <LongPressRecordButton
                onRecordingComplete={handleRecordingComplete}
                onRecordingCancel={handleRecordingCancel}
                onRecordingEdit={handleRecordingEdit}
                onTranscriptionComplete={handleTranscriptionComplete}
                enableSTT={true}
                isEmbedded={isEmbedded} // 🔥 传递嵌入模式信息
                className="flex-1"
                maxRecordingDuration={60} // 1分钟
                warningThreshold={10} // 提前10秒提醒
              />
            </>
          ) : (
            // 文本模式布局：[文件上传] [语音/文本切换] [文本输入框] [发送按钮]
            <>

              <VoiceInput
                onClick={quickbarMode ? onVoiceNavigateToChat : undefined}
              />

              <TextInput
                isEmbedded={isEmbedded}
                quickbarMode={quickbarMode}
                onFocusNavigateToChat={onFocusNavigateToChat}
              />
              <FileUploadButton
                onClick={quickbarMode ? onFileUploadNavigateToChat : undefined}
              />

              <div className="w-2"></div>
              <SendButton
                quickbarMode={quickbarMode}
                onNewTask={onNewTask}
                onNewProject={onNewProject}
              />
            </>
          )}
        </div>
      </div>
    </UppyProvider>
  );
};

export const ChatInputContainer = memo(ChatInputContainerComp);
export default ChatInputContainer;
